/*
 * PublicEnquiryForm — reusable lead capture form
 * ==============================================
 * Used on:
 *   /join                        (standalone page, JoinPage.jsx)
 *   /academy                     (embedded section, AcademyLandingPage)
 *
 * POSTs to /api/marketing/leads/public (no auth).
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Check, RefreshCw, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicEnquiryForm = ({ compact = false, defaultInterest = "", source = "website", partnerSlug = null, hideInterest = false }) => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", player_age: "",
    interested_in: defaultInterest, message: partnerSlug ? `Ενδιαφέρον μέσω συνεργάτη: ${partnerSlug}` : "",
    consent_gdpr: false,
    consent_marketing: false,
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.name.trim()) { setErrorMsg("Το ονοματεπώνυμο είναι υποχρεωτικό"); return; }
    if (!form.phone.trim() && !form.email.trim()) { setErrorMsg("Χρειάζεται τηλέφωνο ή email"); return; }
    if (!form.consent_gdpr) { setErrorMsg("Παρακαλώ αποδεχτείτε την Πολιτική Απορρήτου για να συνεχίσετε"); return; }
    setStatus("loading");
    try {
      await axios.post(`${API}/api/marketing/leads/public`, {
        ...form,
        source: partnerSlug ? "partner_referral" : source,
        player_age: form.player_age ? parseInt(form.player_age) : null,
      });
      setStatus("success");
      setForm({ name: "", phone: "", email: "", player_age: "", interested_in: defaultInterest, message: partnerSlug ? `Ενδιαφέρον μέσω συνεργάτη: ${partnerSlug}` : "", consent_gdpr: false, consent_marketing: false });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Παρουσιάστηκε σφάλμα. Δοκιμάστε ξανά.");
    }
  };

  if (status === "success") {
    return (
      <div className={`bg-emerald-500/10 border border-emerald-500/30 rounded-2xl ${compact ? "p-6" : "p-10"} text-center`} data-testid="enquiry-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
          <Check size={32} className="text-emerald-400" />
        </div>
        <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">Ευχαριστούμε!</h3>
        <p className="text-zinc-300 text-sm mb-4">Το αίτημά σας παραλήφθηκε.<br />Θα επικοινωνήσουμε μαζί σας σύντομα.</p>
        <button onClick={() => setStatus("idle")} className="text-[#F5A623] text-sm hover:underline" data-testid="enquiry-new-btn">
          Νέο Αίτημα
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="public-enquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Ονοματεπώνυμο *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none transition-colors"
            placeholder="Το πλήρες όνομά σας" required data-testid="enquiry-name" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Ηλικία Παίκτη</label>
          <input type="number" min="4" max="99" value={form.player_age} onChange={e => set("player_age", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none transition-colors"
            placeholder="π.χ. 9" data-testid="enquiry-age" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Τηλέφωνο</label>
          <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none transition-colors"
            placeholder="+357 99 000 000" data-testid="enquiry-phone" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Email</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none transition-colors"
            placeholder="you@example.com" data-testid="enquiry-email" />
        </div>
      </div>
      {!hideInterest && (
        <div>
          <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Ενδιαφέρον</label>
          <select value={form.interested_in} onChange={e => set("interested_in", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none transition-colors"
            data-testid="enquiry-interest">
            <option value="">— Επιλέξτε —</option>
            <option value="Ακαδημία U6-U8">Ακαδημία U6-U8</option>
            <option value="Ακαδημία U10-U12">Ακαδημία U10-U12</option>
            <option value="Ακαδημία U14-U16">Ακαδημία U14-U16</option>
            <option value="Δοκιμαστικό Α&apos; Ομάδας">Δοκιμαστικό Α&apos; Ομάδας</option>
            <option value="Summer Camp">Summer Camp</option>
            <option value="Άλλο">Άλλο</option>
          </select>
        </div>
      )}
      <div>
        <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Μήνυμα</label>
        <textarea rows={compact ? 2 : 3} value={form.message} onChange={e => set("message", e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#F5A623] focus:bg-white/10 outline-none resize-none transition-colors"
          placeholder="Πείτε μας πώς μπορούμε να βοηθήσουμε…" data-testid="enquiry-message" />
      </div>

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-sm text-rose-300" data-testid="enquiry-error">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="enquiry-gdpr-label">
          <input
            type="checkbox"
            checked={form.consent_gdpr}
            onChange={e => set("consent_gdpr", e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#F5A623] focus:ring-[#F5A623] focus:ring-offset-0 accent-[#F5A623] cursor-pointer flex-shrink-0"
            data-testid="enquiry-gdpr-checkbox"
          />
          <span className="text-[11px] text-zinc-400 leading-relaxed group-hover:text-zinc-300 transition-colors">
            Συμφωνώ με την επεξεργασία των προσωπικών μου δεδομένων σύμφωνα με την{" "}
            <Link to="/privacy" target="_blank" className="text-[#F5A623] hover:underline" data-testid="enquiry-privacy-link">
              Πολιτική Απορρήτου
            </Link>{" "}
            για την επικοινωνία σχετικά με το ενδιαφέρον μου. <span className="text-rose-400">*</span>
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="enquiry-marketing-label">
          <input
            type="checkbox"
            checked={form.consent_marketing}
            onChange={e => set("consent_marketing", e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-[#F5A623] focus:ring-[#F5A623] focus:ring-offset-0 accent-[#F5A623] cursor-pointer flex-shrink-0"
            data-testid="enquiry-marketing-checkbox"
          />
          <span className="text-[11px] text-zinc-400 leading-relaxed group-hover:text-zinc-300 transition-colors">
            Θέλω να λαμβάνω ενημερώσεις για εκδηλώσεις, δοκιμαστικά, camps και προσφορές της Λευτεριά FC (προαιρετικό).
          </span>
        </label>
      </div>

      <button type="submit" disabled={status === "loading"}
        className="w-full bg-gradient-to-r from-[#F5A623] to-[#FF8C00] hover:from-[#FF8C00] hover:to-[#F5A623] text-black font-bold py-3 rounded-lg text-sm uppercase tracking-widest transition-all disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="enquiry-submit">
        {status === "loading" ? <><RefreshCw size={16} className="animate-spin" /> Αποστολή…</> : "Στειλτε Αιτημα"}
      </button>

      <p className="text-[10px] text-zinc-500 text-center">
        Δείτε τους <Link to="/terms" target="_blank" className="text-[#F5A623] hover:underline">Όρους Χρήσης</Link> και την <Link to="/privacy" target="_blank" className="text-[#F5A623] hover:underline">Πολιτική Απορρήτου</Link>.
      </p>
    </form>
  );
};

export default PublicEnquiryForm;
